import { createRngState, shuffle } from '../rng/rng';
import { propertyCards, chequeCards } from '../deck';
import { PHASES, STARTING_COINS_BY_COUNT, CARDS_REMOVED_BY_COUNT } from '../constants';
import { startRound } from '../actions/auctionActions';

/**
 * Setup. IELLO p3 SETTING UP, with the Gryphon edition's numbers (see
 * docs/RULES_SOURCES.md):
 *
 * - Shuffle the house deck and the cheque deck separately.
 * - 3 players: remove 6 cards from each deck unseen; 4 players: remove 2
 *   from each. That is what makes every round flip exactly one card per
 *   player and come out even.
 * - Each player receives $18,000 (3-4 players) or $14,000 (5-6), hidden.
 * - "The first player is randomly determined."
 *
 * All money is integer thousands, everywhere.
 */
export const createGameState = ({ players, seed = 1 }) => {
  const rng0 = createRngState(seed);
  const [properties, rng1] = shuffle(propertyCards().map((c) => c.id), rng0);
  const [cheques, rng2] = shuffle(chequeCards().map((c) => c.id), rng1);

  const removed = CARDS_REMOVED_BY_COUNT[players.length];
  const coins = STARTING_COINS_BY_COUNT[players.length];

  const [firstSeatRoll, rng3] = shuffle(players.map((_, seat) => seat), rng2);

  const state = {
    meta: {
      seq: 0,
      phase: PHASES.BUYING,
      // Auction rounds and sale rounds each count from 1 in their own phase.
      round: 0,
      // Whose turn it is to bid or pass. Meaningless during the sale phase,
      // where everyone acts at once.
      turnSeat: null,
      // Who opens the current auction round. IELLO p5: "The player who took
      // the biggest card of the previous round becomes the first player."
      openerSeat: firstSeatRoll[0],
      // Set the moment a round ends and cleared when the next one starts, so
      // the table gets to read what happened before the cards reset.
      roundOver: null,
    },

    players: players.map((p, seat) => ({
      id: p.id,
      name: p.name,
      seat,
      isBot: Boolean(p.isBot),
      ...(p.isBot ? { botIndex: p.botIndex ?? seat } : {}),

      // Unspent money in thousands, committed bid included. IELLO p3: "You
      // should keep your coins hidden" - hidden in the UI from everyone
      // else, all game.
      coins,
      // What this player currently has bid in front of them this auction
      // round. Public - IELLO p5: "visible to all".
      bid: 0,
      // Out of the current auction round (IELLO p5: "A player who passes is
      // no longer playing for the rest of the round").
      passed: false,

      // Houses bought in phase 1, spent again in phase 2. Bought in the
      // open, so public.
      properties: [],
      // Cheques won in phase 2. IELLO p7: kept "hidden, face down" until
      // the end.
      cheques: [],
      // The sale phase's secret simultaneous pick.
      pick: null,
    })),

    // Both decks, shuffled, with the removed cards simply never dealt:
    // slicing them off here IS the removal, and nothing ever looks at the
    // tail. "Without looking at them" (IELLO p3) holds for the players; the
    // engine of course knows, the same way a physical deck does.
    propertyDeck: properties.slice(0, properties.length - removed),
    chequeDeck: cheques.slice(0, cheques.length - removed),
    removedPerDeck: removed,

    // Face-up cards in the middle of the table - houses during the buying
    // phase, cheques during the selling phase.
    market: [],

    log: [],
    rngState: rng3,
  };

  return startRound(state);
};
